import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthResp, User } from '@authority-labs/api-interfaces';
import { PERMISSION_KEYS } from '@authority-labs/constants';
import { Path } from '@authority-labs/enums';
import { AppPermissionsService } from '@check-list/shared/services/permissions/permissions.service';
import { firstValueFrom, from, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthenticationModel } from './authentication.model';
import { dropInspectionDrafts } from './inspection-draft-cache';
import { sha256 } from './sha256';

const API = `${environment.API}/auth`;

/** Web Lock that queues token refreshes across this browser's tabs. */
const REFRESH_LOCK = 'auth-refresh';
/** Session-storage key of the PKCE verifier of a Microsoft SSO login. */
const SSO_VERIFIER = 'sso-code-verifier';
/**
 * Session-storage key of the in-app path to return to after the next login.
 * Session storage, not memory: the Microsoft SSO login leaves the page, and
 * a login that ends (refresh refused) reloads it.
 */
const AFTER_LOGIN = 'lcl.afterLogin';
const DEFAULT_AFTER_LOGIN = `/${Path.App}`;

/**
 * The path as a same-origin in-app path, or null. Only relative paths under
 * /app: a scheme, a protocol-relative `//` or a backslash (which some
 * browsers read as a slash) could send the login to another site.
 */
export function safeAfterLoginUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  if (!/^\/app(?:[/?#]|$)/.test(url)) return null;
  // eslint-disable-next-line no-control-regex
  if (url.includes('//') || url.includes('\\') || /[\s\u0000-\u001f]/.test(url))
    return null;
  return url;
}

function storeAfterLoginUrl(url: string | null): void {
  try {
    if (url) sessionStorage.setItem(AFTER_LOGIN, url);
    else sessionStorage.removeItem(AFTER_LOGIN);
  } catch {
    // Storage unavailable: the in-memory copy still serves a same-page login.
  }
}

/** The stored return path, at most once. */
function takeStoredAfterLoginUrl(): string | null {
  try {
    const url = sessionStorage.getItem(AFTER_LOGIN);
    sessionStorage.removeItem(AFTER_LOGIN);
    return safeAfterLoginUrl(url);
  } catch {
    return null;
  }
}

/** The verifier of the SSO login this tab started, at most once. */
function takeSsoVerifier(): string | null {
  try {
    const verifier = sessionStorage.getItem(SSO_VERIFIER);
    sessionStorage.removeItem(SSO_VERIFIER);
    return verifier;
  } catch {
    return null;
  }
}

const base64url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
/**
 * How long a tab waits for another tab's refresh before failing its own as an
 * outage. A refresh itself has no timeout (see postRefresh), so a tab holding
 * the lock this long is frozen (a "Leave site?" prompt, a debugger) or stuck
 * on a connection the browser has not failed yet.
 */
const REFRESH_LOCK_WAIT_MS = 75 * 1000;

/**
 * The refresh answered for a login that this browser no longer holds: a
 * logout or another login (in any tab) replaced it while the call was in
 * flight. The response is dropped, and nobody is logged out for it.
 */
export class RefreshSupersededError extends Error {
  constructor() {
    super('Refresh superseded by a newer login or a logout');
    this.name = 'RefreshSupersededError';
  }
}

/**
 * The server refused the refresh of the login this browser still holds: that
 * login has ended (logout elsewhere, a newer browser login, a revoke).
 */
export class SessionEndedError extends Error {
  constructor() {
    super('The login has ended');
    this.name = 'SessionEndedError';
  }
}

/**
 * Another account signed in from another tab. This tab still acts for the
 * previous account until it reloads, so nothing may go out under the new one.
 */
export class AccountReplacedError extends Error {
  constructor() {
    super('Another account signed in from another tab');
    this.name = 'AccountReplacedError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
  public afterLoginURL = DEFAULT_AFTER_LOGIN;
  public user: User;
  public passwordAttemptsCount = 0;

  /** The refresh in flight in this tab, shared by its callers. */
  private refreshing: Promise<AuthResp> | null = null;
  private followingReplacedAccount = false;

  private readonly storageHandler = (event: StorageEvent) => {
    // localStorage.clear() emits key=null in the other tabs. Do not clear
    // storage again here: another tab may already have started a new login.
    // A tab without a session (e.g. typing on the login page) is left alone.
    if (event.key === null || (event.key === 'token' && !event.newValue)) {
      if (this.user && !location.pathname.startsWith(`/${Path.Login}`)) {
        this.endLocalSession();
      }
    } else if (event.key === 'userId' && this.isAccountReplaced()) {
      // Do not show the previous account's data and edits under the new login.
      this.followReplacedAccount();
    }
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private appPermissionsService: AppPermissionsService
  ) {
    // A stored user of another account than the stored id (a write cut short)
    // would make this tab look replaced on every request.
    this.user = this.storedUser();
    window.addEventListener('storage', this.storageHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageHandler);
  }

  login(username: string, password: string): Observable<User> {
    const authModel: AuthenticationModel = new AuthenticationModel(
      username,
      password
    );

    return this.http
      .post<AuthResp>(`${API}/login`, { ...authModel, clientType: 'web' })
      .pipe(
        tap((_resp) => {
          this.setSession(_resp);
        }),
        map((_resp) => _resp.user)
      );
  }

  /** Sends the user to the login page, to come back to `url` (an /app path). */
  relogin(url: string): void {
    this.afterLoginURL = safeAfterLoginUrl(url) || DEFAULT_AFTER_LOGIN;
    storeAfterLoginUrl(safeAfterLoginUrl(url));
    this.router.navigateByUrl(`/${Path.Login}`);
  }

  /**
   * Where the next login should land: the path kept by relogin() or by a
   * login that ended, once, else the app's main page.
   */
  takeAfterLoginUrl(): string {
    const stored = takeStoredAfterLoginUrl();
    const url = stored || safeAfterLoginUrl(this.afterLoginURL);
    this.afterLoginURL = DEFAULT_AFTER_LOGIN;
    return url || DEFAULT_AFTER_LOGIN;
  }

  /**
   * Ends the login. `sessionEnded`: the server refused the login (not the
   * user leaving), so the next login returns to the page this one was on.
   */
  logout(sessionEnded = false): void {
    const returnTo = sessionEnded
      ? safeAfterLoginUrl(location.pathname + location.search)
      : null;
    // Another account signed in from another tab since: storage is that
    // login's, and this tab must not end it. It only leaves.
    if (this.isAccountReplaced()) {
      this.endLocalSession(returnTo);
      return;
    }
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      // Fire-and-forget: best-effort server-side session revocation. Local
      // storage is cleared regardless, so the user is logged out immediately
      // even if the network call fails.
      this.http
        .post(`${API}/${Path.Logout}`, { refreshToken })
        .subscribe({ error: () => undefined });
    }
    localStorage.clear();
    this.endLocalSession(returnTo);
  }

  /**
   * Keeps `user` until the page unloads: the tab still shows that account
   * (a "Leave site?" prompt can keep it open), and isAccountReplaced() must
   * keep telling it apart from a login made later in another tab.
   * `returnTo` (an /app path) is where the next login in this tab lands.
   */
  private endLocalSession(returnTo: string | null = null): void {
    this.appPermissionsService.clear();
    storeAfterLoginUrl(returnTo);
    setTimeout(() => window.location.replace(`/${Path.Login}`), 200);
  }

  /**
   * A fresh access token for a request that was sent with `staleToken` and
   * refused. One refresh at a time: callers in this tab share the one in
   * flight, and a Web Lock queues the other tabs. A tab that waited reuses the
   * token the first one stored instead of rotating again, because two
   * rotations racing can leave the older refresh token in storage, and the
   * server takes its next use for theft. The refresh runs to completion even
   * if every caller unsubscribes, so a cancelled request cannot strand it.
   */
  refresh(
    staleToken: string | null = this.getAuthToken()
  ): Observable<AuthResp> {
    if (!this.refreshing) {
      this.refreshing = this.withRefreshLock(() =>
        this.rotate(staleToken)
      ).finally(() => (this.refreshing = null));
      // Callers that are still subscribed get the failure; without any, it
      // must not surface as an unhandled rejection.
      this.refreshing.catch(() => undefined);
    }
    return from(this.refreshing);
  }

  private async rotate(staleToken: string | null): Promise<AuthResp> {
    const current = this.getAuthToken();
    if (current && current !== staleToken) {
      // Refreshed meanwhile, by this tab or another one. Never replay a
      // request under a different account that signed in since.
      const owner = this.parseJwt(staleToken)?.userId;
      const claims = this.parseJwt(current);
      if (owner == null || claims?.userId !== owner) {
        throw new RefreshSupersededError();
      }
      // Still good for the replay, or refresh it below.
      if (!claims.exp || claims.exp * 1000 > Date.now() + 10 * 1000) {
        this.user = JSON.parse(localStorage.getItem('user')) as User;
        return {
          token: current,
          refreshToken: this.getRefreshToken(),
          user: this.user
        };
      }
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      // Logged out (in another tab) while this one waited for the lock.
      throw new RefreshSupersededError();
    }
    let resp: AuthResp;
    try {
      resp = await this.postRefresh(refreshToken);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Refused. Only this refresh knows the token it sent: the login it
        // was for has ended if storage still holds that token, and another
        // login (a password change, a new sign-in) replaced it otherwise.
        throw this.getRefreshToken() === refreshToken
          ? new SessionEndedError()
          : new RefreshSupersededError();
      }
      throw error;
    }
    // Logout (including in another tab) can finish while refresh is in
    // flight. A response for that old login must not restore credentials or
    // replay an inspection save under a later login.
    if (!this.isSameLogin(refreshToken, this.getRefreshToken())) {
      throw new RefreshSupersededError();
    }
    this.setSession(resp);
    return resp;
  }

  /**
   * No client timeout, deliberately. The server may commit a rotation the
   * client gave up on; the token the client kept is then retired, and once
   * the server's grace window has passed its next use reads as theft and ends
   * the session, which logs the user out and discards drafts. Waiting is the
   * safe failure: a refresh that never answers keeps this tab's refused
   * requests waiting until the browser fails the connection, and other tabs
   * stop waiting after REFRESH_LOCK_WAIT_MS.
   */
  private postRefresh(refreshToken: string): Promise<AuthResp> {
    return firstValueFrom(
      this.http.post<AuthResp>(`${API}/${Path.Refresh}`, {
        refreshToken,
        clientType: 'web'
      })
    );
  }

  private withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
    // Browsers without Web Locks (Safari before 15.4) refresh unqueued, so
    // their tabs can still race (see browser-session-policy.md).
    if (!navigator.locks) {
      return task();
    }
    // Stop waiting for a frozen or stuck tab and fail like an outage.
    const wait = new AbortController();
    const timer = setTimeout(() => wait.abort(), REFRESH_LOCK_WAIT_MS);
    return navigator.locks.request(
      REFRESH_LOCK,
      { signal: wait.signal },
      () => {
        clearTimeout(timer);
        return task();
      }
    );
  }

  /** True when another account signed in from another tab since this tab loaded. */
  isAccountReplaced(): boolean {
    const stored = this.getUserId();
    if (!this.user) {
      // A tab that reached the app without its own login (another tab signed
      // in meanwhile) acts for the stored account from its first request on.
      this.user = this.storedUser();
      return false;
    }
    return !!stored && stored !== String(this.user.id);
  }

  /** The stored user, unless its id disagrees with the stored one. */
  private storedUser(): User | null {
    const stored = JSON.parse(localStorage.getItem('user')) as User;
    return stored && String(stored.id) === localStorage.getItem('userId')
      ? stored
      : null;
  }

  /**
   * Reloads this tab as the account now signed in, once its own was replaced
   * from another tab. Called on that tab's storage event, and by the
   * interceptor for a tab that missed the event (restored from the back-forward
   * cache, or frozen meanwhile). A tab on the login page stays.
   */
  followReplacedAccount(): void {
    // Once per page: if the user stays (a "Leave site?" prompt to keep
    // unsaved edits), later requests fail quietly instead of asking again.
    if (this.followingReplacedAccount) return;
    if (!location.pathname.startsWith(`/${Path.Login}`)) {
      this.followingReplacedAccount = true;
      this.reload();
    }
  }

  private reload(): void {
    window.location.reload();
  }

  isAuthenticated(): boolean {
    // A server-side render (the inspection PDF export opens
    // /app/preview/<id>?token=… in browserless) brings its token in the URL to
    // an empty browser. Only an empty one: there it can replace no login and
    // drop no drafts, so a crafted link has nothing to take.
    // The SSO landing on /login goes through completeSsoLogin() instead.
    const token = new URLSearchParams(location.search).get('token');
    if (
      token &&
      !this.getAuthToken() &&
      !location.pathname.startsWith(`/${Path.Login}`)
    ) {
      const user = this.parseJwt(token);
      if (user?.userId) {
        user.id = user.userId;
        this.setSession({ token, user });
      }
    }
    return !!localStorage.getItem('userId') && !!localStorage.getItem('token');
  }

  /**
   * Completes a Microsoft SSO login on the page the callback redirected to,
   * /login?code=…: redeems the one-time code with the verifier this tab kept
   * when it started the flow (signInWithMicrosoft). The API creates the
   * session only then, so a flow another site forced on this browser, whose
   * tab holds no verifier, creates nothing and signs no browser out.
   *
   * The code leaves the address bar and the history first. Resolves to null
   * when signed in, or to the error to show (login.microsoftError.*).
   */
  async completeSsoLogin(): Promise<string | null> {
    const code = new URLSearchParams(location.search).get('code');
    history.replaceState(history.state, '', location.pathname);
    const codeVerifier = takeSsoVerifier();
    if (!code || !codeVerifier) {
      return 'auth_failed';
    }
    try {
      const resp = await firstValueFrom(
        this.http.post<AuthResp>(`${API}/microsoft/exchange`, {
          code,
          codeVerifier
        })
      );
      this.setSession(resp);
      return null;
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : 0;
      return status === 401
        ? 'account_disabled'
        : status === 404
        ? 'user_not_found'
        : status === 409
        ? 'login_superseded'
        : 'auth_failed';
    }
  }

  getAuthToken(): string {
    return localStorage.getItem('token');
  }

  getRefreshToken(): string {
    return localStorage.getItem('refreshToken');
  }

  getUserId(): string {
    return localStorage.getItem('userId');
  }

  getUser(): User {
    return this.user;
  }

  getUsername(): string {
    return this.user?.username;
  }

  isManager(): boolean {
    return this.appPermissionsService.hasAny(
      PERMISSION_KEYS.ORDERS_EDIT,
      PERMISSION_KEYS.USERS_MANAGE
    );
  }

  isSuperAdmin(): boolean {
    return this.appPermissionsService.has(PERMISSION_KEYS.PERMISSIONS_MANAGE);
  }

  restoreAccess(username: string): Observable<void> {
    return this.http.put<void>(`${API}/${Path.RestoreAccess}/${username}`, {});
  }

  getMicrosoftLoginUrl(codeChallenge: string): string {
    return `${API}/microsoft?platform=web&code_challenge=${codeChallenge}`;
  }

  /**
   * Starts the Microsoft SSO flow (PKCE, RFC 7636): this tab keeps a random
   * verifier and sends its SHA-256 challenge; completeSsoLogin() redeems the
   * code with it.
   */
  async signInWithMicrosoft(): Promise<void> {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const codeVerifier = base64url(bytes);
    const encoded = new TextEncoder().encode(codeVerifier);
    // crypto.subtle exists in secure contexts only (HTTPS, localhost); a dev
    // server on a LAN address over plain HTTP lacks it (and Web Locks).
    const digest = crypto.subtle
      ? new Uint8Array(await crypto.subtle.digest('SHA-256', encoded))
      : sha256(encoded);
    sessionStorage.setItem(SSO_VERIFIER, codeVerifier);
    window.location.href = this.getMicrosoftLoginUrl(base64url(digest));
  }

  passwordChange(
    newPass: string,
    userId: string,
    pass: string
  ): Observable<AuthResp> {
    return this.http
      .patch<AuthResp>(`${API}/${Path.ChangePass}`, {
        pass,
        userId,
        newPass,
        clientType: 'web'
      })
      .pipe(
        tap((_resp) => {
          this.setSession(_resp);
        })
      );
  }

  passwordRestore(token: string, pass: string): Observable<AuthResp> {
    return this.http
      .patch<AuthResp>(`${API}/${Path.RestoreAccess}`, {
        token,
        pass,
        clientType: 'web'
      })
      .pipe(
        tap((_resp) => {
          this.setSession(_resp);
        })
      );
  }

  private setSession(authResult: AuthResp) {
    // Only the same account continuing (refresh, password change) keeps its
    // drafts; another account's go, first so their space is free.
    const otherAccount = this.getUserId() !== String(authResult.user.id);
    if (otherAccount) {
      dropInspectionDrafts();
      // Never the previous account's refresh token beside this account's
      // login: its refresh would switch the browser back.
      if (!authResult.refreshToken) {
        localStorage.removeItem('refreshToken');
      }
    }
    const writes: [string, string][] = [
      ['userId', String(authResult.user.id)],
      ['user', JSON.stringify(authResult.user)],
      ['token', authResult.token],
      ...(authResult.refreshToken
        ? [['refreshToken', authResult.refreshToken] as [string, string]]
        : [])
    ];
    // Another account: its id first, so other tabs (isAccountReplaced) stop
    // writing drafts and sending requests before its token is there. The same
    // account: the tokens first, since the server has retired the old ones.
    if (!otherAccount) {
      writes.reverse();
    }
    try {
      for (const [key, value] of writes) {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      // Storage full. Never leave one account's id beside another's user; the
      // same account keeps whatever was written, the tokens first.
      if (otherAccount) {
        for (const [key] of writes) {
          localStorage.removeItem(key);
        }
      }
      throw error;
    }
    this.user = authResult.user;
    this.passwordAttemptsCount = 0;
    // Again last: drafts a tab of the previous account wrote before it saw
    // the new account.
    if (otherAccount) {
      dropInspectionDrafts();
    }
  }

  /** True when both refresh tokens belong to one login session. */
  isSameLogin(refreshToken: string, other: string): boolean {
    if (!refreshToken || !other) return false;
    const session = this.parseJwt(refreshToken)?.sessionUid;
    return !!session && this.parseJwt(other)?.sessionUid === session;
  }

  /** The claims of a JWT, or null. Its payload is base64url-encoded UTF-8. */
  private parseJwt(token) {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const utf8 = Array.from(
        atob(base64),
        (c: string) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`
      ).join('');
      return JSON.parse(decodeURIComponent(utf8));
    } catch (e) {
      return null;
    }
  }
}
